// src/utils/toast.ts
import { createApp, h, type App } from 'vue'
import ToastComponent from '@/components/ToastDialog.vue'

interface ToastOptions {
    message: string
    duration?: number
    position?: 'top' | 'bottom' | 'center'
    closeable?: boolean
}

class ToastManager {
    private static instance: ToastManager
    private toasts: Map<number, { app: App; container: HTMLElement }> = new Map()
    private nextId = 1

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    static getInstance(): ToastManager {
        if (!ToastManager.instance) {
            ToastManager.instance = new ToastManager()
        }
        return ToastManager.instance
    }

    show(message: string, options?: Omit<ToastOptions, 'message'>): number {
        const id = this.nextId++
        const mergedOptions: ToastOptions = {
            message,
            duration: 3000,
            position: 'top',
            closeable: true,
            ...options
        }

        // 创建容器
        const container = document.createElement('div')
        container.className = 'toast-container'
        document.body.appendChild(container)

        // 创建Vue应用实例
        const app = createApp({
            render: () => h(ToastComponent, {
                ...mergedOptions,
                onClose: () => {
                    this.removeToast(id)
                }
            })
        })

        app.mount(container)

        this.toasts.set(id, { app, container })
        return id
    }

    removeToast(id: number) {
        const toast = this.toasts.get(id)
        if (toast) {
            setTimeout(() => {
                toast.app.unmount()
                document.body.removeChild(toast.container)
            }, 300)
            this.toasts.delete(id)
        }
    }

    clearAll() {
        this.toasts.forEach((toast) => {
            toast.app.unmount()
            document.body.removeChild(toast.container)
        })
        this.toasts.clear()
    }
}

// 创建全局Toast函数
const toast = {
    show: (message: string, options?: Omit<ToastOptions, 'message'>) =>
        ToastManager.getInstance().show(message, options),

    // 快捷方法
    info: (message: string, duration?: number) =>
        ToastManager.getInstance().show(message, { duration }),

    success: (message: string, duration?: number) =>
        ToastManager.getInstance().show(message, { duration }),

    error: (message: string, duration?: number) =>
        ToastManager.getInstance().show(message, { duration }),

    warning: (message: string, duration?: number) =>
        ToastManager.getInstance().show(message, { duration }),

    clearAll: () => ToastManager.getInstance().clearAll()
}

export default toast
